import { supabase } from '../lib/supabase';
import type { AccountType, TransactionType } from '../types';

// 正規化されたトランザクションデータの型定義
export interface NormalizedTransactionData {
  account_type: AccountType;
  type: TransactionType;
  amount: number;
  category: string;
  transaction_date: string;
  description?: string; // 摘要（任意）
  reference_number?: string;
  source: 'manual' | 'file';
  source_metadata?: {
    file_name?: string;
    row_number?: number;
    original_data?: any;
  };
}

// 取り込み結果の型定義
export interface IngestionResult {
  success: NormalizedTransactionData[];
  errors: Array<NormalizedTransactionData & { error: string }>;
  duplicates: NormalizedTransactionData[];
  total: number;
  summary: {
    successCount: number;
    errorCount: number;
    duplicateCount: number;
  };
}

// データ検証ルール
export class TransactionValidator {
  static validate(data: NormalizedTransactionData): string | null {
    // 必須フィールドのチェック
    if (!data.transaction_date || data.transaction_date.trim() === '') {
      return '取引日は必須です';
    }


    if (!data.amount || data.amount <= 0) {
      return '金額は正の数値である必要があります';
    }

    if (!data.category || data.category.trim() === '') {
      return 'カテゴリは必須です';
    }

    if (!['general', 'reserve'].includes(data.account_type)) {
      return '会計種別が不正です';
    }

    if (!['income', 'expense'].includes(data.type)) {
      return '取引種別が不正です';
    }

    // 日付フォーマットのチェック
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(data.transaction_date)) {
      return '日付はYYYY-MM-DD形式である必要があります';
    }

    const date = new Date(data.transaction_date);
    if (isNaN(date.getTime())) {
      return '有効な日付である必要があります';
    }

    // 金額の上限チェック（1億円）
    if (data.amount > 100000000) {
      return '金額が上限を超えています';
    }

    return null; // 検証成功
  }
}

// 重複チェック機能
export class DuplicateChecker {
  static async checkDuplicate(
    data: NormalizedTransactionData,
    organizationId: string
  ): Promise<boolean> {
    try {
      const { data: existing, error } = await supabase
        .from('transactions')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('transaction_date', data.transaction_date)
        .eq('amount', data.amount)
        .eq('category', data.category)
        .eq('account_type', data.account_type)
        .limit(1);

      if (error) {
        console.error('重複チェックエラー:', error);
        return false; // エラーの場合は重複なしとして扱う
      }

      return existing && existing.length > 0;
    } catch (error) {
      console.error('重複チェック例外:', error);
      return false;
    }
  }
}

// トランザクション保存機能
export class TransactionSaver {
  static async saveTransaction(
    data: NormalizedTransactionData,
    organizationId: string,
    userId: string
  ): Promise<void> {
    // 取引日から年と月を抽出
    const transactionDate = new Date(data.transaction_date);
    const year = transactionDate.getFullYear();
    const month = transactionDate.getMonth() + 1; // 0-indexed to 1-indexed

    const { error } = await supabase
      .from('transactions')
      .insert({
        organization_id: organizationId,
        account_type: data.account_type,
        type: data.type,
        amount: Math.round(data.amount), // 整数に変換
        category: data.category,
        transaction_date: data.transaction_date,
        description: data.description || null,
        reference_number: data.reference_number || null,
        year: year,
        month: month,
        created_by: userId
      });

    if (error) {
      throw new Error(`保存エラー: ${error.message}`);
    }
  }
}

// メインの取り込みサービス
export class TransactionIngestionService {
  private organizationId: string;
  private userId: string;

  constructor(organizationId: string, userId: string) {
    this.organizationId = organizationId;
    this.userId = userId;
  }

  /**
   * 複数のトランザクションデータを一括で取り込む
   */
  async ingestTransactions(
    transactions: NormalizedTransactionData[]
  ): Promise<IngestionResult> {
    const result: IngestionResult = {
      success: [],
      errors: [],
      duplicates: [],
      total: transactions.length,
      summary: {
        successCount: 0,
        errorCount: 0,
        duplicateCount: 0
      }
    };

    for (const transaction of transactions) {
      try {
        // 1. データ検証
        const validationError = TransactionValidator.validate(transaction);
        if (validationError) {
          result.errors.push({
            ...transaction,
            error: validationError
          });
          continue;
        }

        // 2. 重複チェック
        const isDuplicate = await DuplicateChecker.checkDuplicate(
          transaction,
          this.organizationId
        );
        if (isDuplicate) {
          result.duplicates.push(transaction);
          continue;
        }

        // 3. データ保存
        await TransactionSaver.saveTransaction(
          transaction,
          this.organizationId,
          this.userId
        );

        result.success.push(transaction);
      } catch (error) {
        result.errors.push({
          ...transaction,
          error: error instanceof Error ? error.message : '不明なエラー'
        });
      }
    }

    // サマリーを計算
    result.summary = {
      successCount: result.success.length,
      errorCount: result.errors.length,
      duplicateCount: result.duplicates.length
    };

    return result;
  }

  /**
   * 単一のトランザクションを取り込む
   */
  async ingestSingleTransaction(
    transaction: NormalizedTransactionData
  ): Promise<IngestionResult> {
    return this.ingestTransactions([transaction]);
  }
}

// ユーティリティ関数: 手動入力データを正規化
export function normalizeManualInput(
  formData: {
    account_type: AccountType;
    type: TransactionType;
    amount: number;
    category: string;
    transaction_date: string;
    description?: string;
    reference_number?: string;
  }
): NormalizedTransactionData {
  return {
    account_type: formData.account_type,
    type: formData.type,
    amount: formData.amount,
    category: formData.category.trim(),
    transaction_date: formData.transaction_date,
    description: formData.description?.trim() || undefined,
    reference_number: formData.reference_number?.trim() || undefined,
    source: 'manual'
  };
}
